import { API_PATHS, ENV_CONFIG } from "../../config";
import { normalizeApiListResponse } from "../../utils/common/helpers";
import { apiRequest } from "./axios";

/**
 * T&M Log API functions
 * Functions for managing T&M reports and pricing
 */

/**
 * List all T&M reports for the log table.
 *
 * @returns {Promise<Array|Object>}
 */
export async function getTMLogs() {
  const url = "/tmlog/reports/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Slim T&M rows for Projects Home / Project Overview.
 *
 * @param {Object} [options]
 * @param {number|string} [options.jobId] Optional job PK to scope the list.
 * @returns {Promise<Array<Object>>}
 */
export async function getTMProjectSummaries({ jobId } = {}) {
  const params = new URLSearchParams();
  if (jobId != null && jobId !== "") {
    params.set("job", String(jobId));
  }
  const query = params.toString();
  const url = `${API_PATHS.TMLOG.PROJECT_SUMMARIES}${query ? `?${query}` : ""}`;
  const data = await apiRequest(url, null, { method: "GET" });
  return normalizeApiListResponse(data);
}

/**
 * Fetch one T&M report by primary key.
 *
 * @param {number|string} id TMReport PK.
 * @returns {Promise<Object>}
 */
export async function getTMLogById(id) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Absolute URL for the authenticated on-demand T&M report PDF.
 *
 * @param {number|string} reportId - Saved TMReport primary key.
 * @returns {string} Full PDF endpoint URL.
 */
export function getTMReportPdfUrl(reportId) {
  return `${ENV_CONFIG.API_URL}${API_PATHS.TMLOG.REPORT_PDF(reportId)}`;
}

/**
 * Absolute URL for the authenticated on-demand T&M priced PDF.
 *
 * @param {number|string} priceId - Saved TMPrice primary key.
 * @returns {string} Full PDF endpoint URL.
 */
export function getTMPricePdfUrl(priceId) {
  return `${ENV_CONFIG.API_URL}${API_PATHS.TMLOG.PRICE_PDF(priceId)}`;
}

/**
 * Resolve the Egnyte/raw folder path used to list or upload report attachments.
 *
 * @param {number|string} reportId TMReport PK.
 * @returns {Promise<Object>}
 */
export async function getTMAttachmentFolderPath(reportId) {
  const url = `/tmlog/reports/${reportId}/attachment-folder-path/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Create a T&M report.
 *
 * @param {Object} data Report create payload.
 * @returns {Promise<Object>}
 */
export async function createTMReport(data) {
  const url = "/tmlog/reports/";
  return await apiRequest(url, data, { method: "POST" });
}

/**
 * Patch an existing T&M report.
 *
 * @param {number|string} id TMReport PK.
 * @param {Object} data Partial report fields.
 * @returns {Promise<Object>}
 */
export async function updateTMReport(id, data) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

/**
 * Delete a T&M report.
 *
 * @param {number|string} id TMReport PK.
 * @returns {Promise<Object>}
 */
export async function deleteTMReport(id) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}

/**
 * Toggle the submitted flag on a T&M report.
 *
 * @param {number|string} id TMReport PK.
 * @returns {Promise<Object>}
 */
export async function toggleTMReportSubmitted(id) {
  const url = `/tmlog/reports/${id}/toggle-submitted/`;
  return await apiRequest(url, {}, { method: "POST" });
}

/**
 * Fetch a T&M price snapshot by TMPrice PK.
 *
 * @param {number|string} priceId TMPrice PK.
 * @returns {Promise<Object>}
 */
export async function getTMPrice(priceId) {
  const url = `/tmlog/prices/${priceId}/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Fetch pricing for a report (creates lookup by original TMReport PK).
 *
 * @param {number|string} reportId TMReport PK.
 * @returns {Promise<Object>}
 */
export async function getTMPriceByReport(reportId) {
  const url = `/tmlog/prices/by-report/${reportId}/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Create a pricing snapshot from an existing T&M report.
 *
 * @param {number|string} reportId TMReport PK copied into the payload.
 * @param {Object} data Additional price fields.
 * @returns {Promise<Object>}
 */
export async function createTMPrice(reportId, data) {
  const url = "/tmlog/prices/";
  return await apiRequest(url, { ...data, report: reportId }, { method: "POST" });
}

/**
 * Patch a T&M price snapshot.
 *
 * @param {number|string} id TMPrice PK.
 * @param {Object} data Partial price fields.
 * @returns {Promise<Object>}
 */
export async function updateTMPrice(id, data) {
  const url = `/tmlog/prices/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

/**
 * Fetch T&M reference catalogs (contractors, PCO numbers, etc.).
 *
 * @returns {Promise<Object>}
 */
export async function getTMReferenceData() {
  const url = "/tmlog/reference/reference_data/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Next available T&M ID for a job number (any string, e.g. "668-05").
 *
 * @param {string|number} jobNumber Job number as stored on the report.
 * @returns {Promise<Object>}
 */
export async function getNextTMId(jobNumber) {
  const encoded = encodeURIComponent(String(jobNumber));
  const url = `/tmlog/generate-tm-id/${encoded}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// ===== Reference Data API Functions =====

/**
 * DEPRECATED. Use `getUsersByRole({ active: true })` from `services/api/core`
 * instead. Kept for backward compatibility; prefer `TMLogContext.workers`.
 *
 * @returns {Promise<Array>}
 */
export async function getWorkers() {
  const { getUsersByRole } = await import("../api/core");
  return await getUsersByRole({ active: true });
}

/**
 * Materials catalog for T&M material line items (excludes Heavy Equipment / Unknown).
 *
 * @returns {Promise<Array>}
 */
export async function getMaterials() {
  const url = "/tmlog/reference/materials/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Heavy-equipment catalog for T&M equipment line items.
 *
 * @returns {Promise<Array>}
 */
export async function getEquipment() {
  const url = "/tmlog/reference/equipment/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Mix-design catalog for T&M concrete line items.
 *
 * @returns {Promise<Array>}
 */
export async function getMixDesigns() {
  const url = "/tmlog/reference/mix_designs/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Subcontractor catalog for T&M subcontractor line items.
 *
 * @returns {Promise<Array>}
 */
export async function getSubcontractors() {
  const url = "/tmlog/reference/subcontractors/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Dumping options (SubContractor-backed) for T&M dumping line items.
 *
 * @returns {Promise<Array>}
 */
export async function getDumping() {
  const url = "/tmlog/reference/dumping/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Units of measure for T&M material line items.
 *
 * @returns {Promise<Array>}
 */
export async function getUnits() {
  const url = "/tmlog/reference/units/";
  return await apiRequest(url, null, { method: "GET" });
}

// ===== Inspector API Functions =====

/**
 * PUBLIC (no auth): load inspector payload by token for the inspector page.
 *
 * @param {string} token Inspector access token.
 * @returns {Promise<Object>}
 */
export async function getInspectorByToken(token) {
  const url = `/tmlog/inspector/by-token/${token}/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * PUBLIC (no auth): submit inspector signature and comments.
 *
 * @param {Object} data Signature payload.
 * @returns {Promise<Object>}
 */
export async function submitInspectorSignature(data) {
  const url = "/tmlog/inspector/submit/";
  return await apiRequest(url, data, { method: "POST" });
}

/**
 * Authenticated: send a report to an inspector.
 *
 * @param {number|string} reportId TMReport PK.
 * @param {Object} data Inspector send payload.
 * @returns {Promise<Object>}
 */
export async function sendToInspector(reportId, data) {
  const url = `/tmlog/reports/${reportId}/send-to-inspector/`;
  return await apiRequest(url, data, { method: "POST" });
}

/**
 * Authenticated: toggle the locked flag on a T&M report.
 *
 * @param {number|string} id TMReport PK.
 * @returns {Promise<Object>}
 */
export async function toggleTMReportLocked(id) {
  const url = `/tmlog/reports/${id}/toggle-locked/`;
  return await apiRequest(url, {}, { method: "POST" });
}
